import {
  Box,
  Button,
  Divider,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import styled from '@emotion/styled';
import { tokens } from '../../../theme';
import {
  decreaseCount,
  increaseCount,
  removeFromCart,
  setIsCartOpen,
} from '../../../features/cart/cartApiSlice';
import { useNavigate } from 'react-router-dom';

const FlexBox = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const siteFont = 'Poppins, sans-serif';

const getCartItemId = (item) => item?.cartId || item?.id || item?._id || item?.slug;

const getCartItemImage = (item) => {
  if (item?.imageUrl) return item.imageUrl;

  const image =
    item?.primaryImage ||
    item?.images?.find((imageItem) => imageItem?.isPrimary) ||
    item?.images?.[0];

  if (image?.url) return image.url;

  // Old Contentful fallback, so old cart items do not explode.
  if (image?.fields?.file?.url) return image.fields.file.url;

  return '';
};

const getCartItemPrice = (item) => {
  if (typeof item?.price === 'number') return item.price;

  if (typeof item?.pricing?.basePrice === 'number') return item.pricing.basePrice;

  const parsedPrice = Number(item?.price || item?.pricing?.basePrice);

  return Number.isNaN(parsedPrice) ? 0 : parsedPrice;
};

const getCartItemPriceText = (item) => {
  if (item?.displayPriceText) return item.displayPriceText;
  if (item?.pricing?.displayPriceText) return item.pricing.displayPriceText;

  const price = getCartItemPrice(item);

  if (!price) return 'Contact for pricing';

  return `$${new Intl.NumberFormat('en-US').format(price)}`;
};

const CartMenu = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const cart = useSelector((state) => state.cart.cart);
  const isCartOpen = useSelector((state) => state.cart.isCartOpen);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery('(max-width:700px)');

  const closeCart = () => dispatch(setIsCartOpen(false));

  const totalPrice = cart.reduce((total, item) => {
    return total + (item.count || 1) * getCartItemPrice(item);
  }, 0);

  const formattedTotalPrice = new Intl.NumberFormat('en-US').format(totalPrice);

  return (
    <Box
      display={isCartOpen ? 'block' : 'none'}
      position="fixed"
      zIndex={1300}
      width="100%"
      height="100%"
      left="0"
      top="0"
      overflow="auto"
      onClick={closeCart}
      sx={{
        backgroundColor: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <Box
        onClick={(event) => event.stopPropagation()}
        position="fixed"
        top={{ xs: 0, sm: '84px' }}
        right={{ xs: 0, sm: '24px' }}
        bottom={{ xs: 0, sm: 'auto' }}
        width={{ xs: '100%', sm: 'min(470px, calc(100vw - 48px))' }}
        height={{ xs: '100%', sm: 'auto' }}
        maxHeight={{ xs: '100%', sm: 'calc(100vh - 110px)' }}
        sx={{
          fontFamily: siteFont,
          color: 'white',
          backgroundColor: isMobile
            ? colors.primary[200]
            : 'rgba(18, 24, 32, 0.94)',
          borderLeft: { xs: 'none', sm: '1px solid rgba(255,255,255,0.12)' },
          border: { xs: 'none', sm: '1px solid rgba(255,255,255,0.12)' },
          borderRadius: { xs: 0, sm: '24px' },
          boxShadow: {
            xs: 'none',
            sm: '0 24px 80px rgba(0,0,0,0.45)',
          },
          backdropFilter: 'blur(16px)',
          overflow: 'hidden',
          '& .MuiTypography-root': {
            fontFamily: siteFont,
          },
          '& .MuiButton-root': {
            fontFamily: siteFont,
          },
        }}
      >
        <Box
          sx={{
            p: { xs: '24px', sm: '26px' },
            height: { xs: '100%', sm: 'auto' },
            maxHeight: { xs: '100%', sm: 'calc(100vh - 110px)' },
            overflow: 'auto',
          }}
        >
          <FlexBox mb="18px">
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  letterSpacing: '-0.04em',
                  color: 'white',
                  lineHeight: 1,
                }}
              >
                Cart
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.82)',
                  mt: 0.75,
                  fontWeight: 600,
                }}
              >
                {cart.length} {cart.length === 1 ? 'item' : 'items'} ready for checkout
              </Typography>
            </Box>

            <IconButton
              onClick={closeCart}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.12)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.14)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </FlexBox>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.10)', mb: 2 }} />

          {cart.length === 0 ? (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: '20px',
                backgroundColor: 'rgba(255,255,255,0.055)',
                border: '1px solid rgba(255,255,255,0.10)',
              }}
            >
              <Typography variant="h5" fontWeight="900" mb={1}>
                Your cart is empty
              </Typography>

              <Typography color={colors.grey[300]} mb={3}>
                Add ATG hardware or software products from the catalog.
              </Typography>

              <Button
                onClick={() => {
                  navigate('/store');
                  closeCart();
                }}
                sx={{
                  backgroundColor: colors.primary[500],
                  color: colors.greenAccent[400],
                  borderRadius: '999px',
                  px: 3,
                  py: 1.25,
                  fontWeight: 800,
                  '&:hover': {
                    backgroundColor: colors.primary[400],
                  },
                }}
              >
                Browse Products
              </Button>
            </Box>
          ) : (
            <Box>
              {cart.map((item) => {
                const itemId = getCartItemId(item);
                const imageUrl = getCartItemImage(item);
                const priceText = getCartItemPriceText(item);

                return (
                  <Box
                    key={`${item.name}-${itemId}`}
                    sx={{
                      mb: 1.5,
                      p: 1.75,
                      borderRadius: '20px',
                      backgroundColor: 'rgba(255,255,255,0.065)',
                      border: '1px solid rgba(255,255,255,0.10)',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.16)',
                    }}
                  >
                    <FlexBox alignItems="flex-start" gap={2}>
                      <Box flex="0 0 92px">
                        {imageUrl ? (
                          <Box
                            component="img"
                            alt={item?.name}
                            src={imageUrl}
                            sx={{
                              width: '92px',
                              height: '92px',
                              objectFit: 'cover',
                              borderRadius: '16px',
                              border: '1px solid rgba(255,255,255,0.14)',
                              backgroundColor: 'rgba(0,0,0,0.25)',
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '92px',
                              height: '92px',
                              borderRadius: '16px',
                              border: '1px solid rgba(255,255,255,0.14)',
                              backgroundColor: 'rgba(0,0,0,0.20)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: colors.greenAccent[300],
                              fontWeight: 900,
                              fontFamily: siteFont,
                              fontSize: '0.9rem',
                            }}
                          >
                            USFuel
                          </Box>
                        )}
                      </Box>

                      <Box flex="1 1 auto" minWidth={0}>
                        <FlexBox alignItems="flex-start" gap={1}>
                          <Typography
                            sx={{
                              pr: 1,
                              color: 'white',
                              fontWeight: 900,
                              fontSize: '0.98rem',
                              lineHeight: 1.25,
                              letterSpacing: '-0.02em',
                            }}
                          >
                            {item.name}
                          </Typography>

                          <IconButton
                            onClick={() => dispatch(removeFromCart({ id: itemId }))}
                            sx={{
                              color: colors.grey[300],
                              p: 0.5,
                              '&:hover': {
                                color: 'white',
                                backgroundColor: 'rgba(255,255,255,0.08)',
                              },
                            }}
                            size="small"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </FlexBox>

                        <Typography
                          variant="body2"
                          sx={{
                            color: 'rgba(255,255,255,0.80)',
                            mt: 0.75,
                            mb: 1.5,
                            lineHeight: 1.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {item.shortDescription || item.tagline || item.description}
                        </Typography>

                        <FlexBox
                          sx={{
                            gap: 1.5,
                            alignItems: 'flex-end',
                            width: '100%',
                          }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            flexShrink={0}
                            border="1px solid rgba(255,255,255,0.14)"
                            borderRadius="999px"
                            backgroundColor="rgba(0,0,0,0.20)"
                          >
                            <IconButton
                              onClick={() => dispatch(decreaseCount({ id: itemId }))}
                              sx={{
                                color: 'white',
                                p: 0.75,
                                '&:hover': {
                                  backgroundColor: 'rgba(255,255,255,0.10)',
                                },
                              }}
                              size="small"
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>

                            <Typography
                              sx={{
                                px: 1.1,
                                minWidth: '28px',
                                textAlign: 'center',
                                fontWeight: 800,
                              }}
                            >
                              {item.count || 1}
                            </Typography>

                            <IconButton
                              onClick={() => dispatch(increaseCount({ id: itemId }))}
                              sx={{
                                color: 'white',
                                p: 0.75,
                                '&:hover': {
                                  backgroundColor: 'rgba(255,255,255,0.10)',
                                },
                              }}
                              size="small"
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>

                          <Typography
                            sx={{
                              flex: '1 1 0',
                              minWidth: 0,
                              maxWidth: '210px',
                              fontWeight: 900,
                              color: colors.greenAccent[300],
                              textAlign: 'right',
                              lineHeight: 1.35,

                              // Allow long display prices like:
                              // "$2,000 per unit + $250/year per ATG"
                              // to stay inside the card.
                              whiteSpace: 'normal',
                              overflowWrap: 'anywhere',
                              wordBreak: 'normal',
                            }}
                          >
                            {priceText}
                          </Typography>
                        </FlexBox>
                      </Box>
                    </FlexBox>
                  </Box>
                );
              })}
            </Box>
          )}

          <Box
            sx={{
              mt: 2.5,
              p: 2.25,
              borderRadius: '20px',
              backgroundColor: 'rgba(0,0,0,0.22)',
              border: '1px solid rgba(255,255,255,0.10)',
            }}
          >
            <FlexBox>
              <Box>
                <Typography
                  sx={{
                    color: colors.grey[300],
                    fontSize: '0.82rem',
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                  }}
                >
                  SUBTOTAL
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    color: 'white',
                    fontWeight: 900,
                    letterSpacing: '-0.04em',
                    mt: 0.5,
                  }}
                >
                  {totalPrice > 0 ? `$${formattedTotalPrice}` : 'Contact'}
                </Typography>
              </Box>

              <Typography
                sx={{
                  color: colors.grey[700],
                  fontSize: '0.82rem',
                  textAlign: 'right',
                  maxWidth: '160px',
                  lineHeight: 1.4,
                }}
              >
                Taxes and shipping calculated during checkout.
              </Typography>
            </FlexBox>

            <Button
              disabled={cart.length === 0}
              sx={{
                backgroundColor: colors.primary[500],
                color: colors.greenAccent[400],
                borderRadius: '999px',
                minWidth: '100%',
                padding: '16px 28px',
                mt: 2.25,
                fontWeight: 900,
                fontSize: '1rem',
                letterSpacing: '0.04em',
                boxShadow: '0 14px 32px rgba(0,0,0,0.25)',
                '&:hover': {
                  backgroundColor: colors.primary[400],
                },
                '&.Mui-disabled': {
                  color: colors.grey[500],
                  backgroundColor: 'rgba(255,255,255,0.08)',
                },
              }}
              onClick={() => {
                navigate('/checkout');
                closeCart();
              }}
            >
              CHECKOUT
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CartMenu;